import { Swap } from '@phosphor-icons/react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { bottomScrollTop } from '~/features/chat/open-at-bottom-logic';
import { useReadingPrefsStore } from '~/features/chat/reading-prefs';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { capacityForWidth, capacityOf, findWindow, needsChoice, type WindowEntry } from '~/lib/stores/window-rules';
import { useWindowStore } from '~/lib/stores/window-store';
import { cn } from '~/lib/utils';
import { useWindowSlots } from './window-system';
import { PRO_BUILDER_WINDOW_ID } from './window-titles';

/**
 * FRONT-3146: the layer windows render into. It covers the content area (the
 * app shell's main card, right of the sidebar and above the window bar), inset
 * by the shell's own seam (`--frame-gap`, windows carry `--frame-radius`
 * themselves). One visible window fills it; more sit side by side in equal columns.
 *
 * Each window gets a slot element that exists for as long as the window does,
 * whether it is shown or minimized: a minimized window is `display: none`,
 * never unmounted, so a running chat keeps streaming and the terminal keeps
 * its place. Pro Builder's slot additionally outlives its window (closing only
 * hides it — its terminal is a running Claude Code, see
 * `pro-builder-open-store.ts`).
 *
 * Pointer/focus entry into a slot marks that window as the one with attention,
 * which is what decides which window Escape minimizes.
 *
 * The layer also measures the content card it sits in: when it is too narrow
 * for two windows side by side, the model drops to one column
 * (`capacityForWidth` / `setCapacity`, window-rules.ts).
 */
export function WindowLayer() {
  const windows = useWindowStore((s) => s.windows);
  const proBuilderEverOpened = useProBuilderOpenStore((s) => s.everOpened);
  const layerRef = useRef<HTMLDivElement | null>(null);
  useCapacityFromWidth(layerRef);
  return <WindowLayerView windows={windows} proBuilderEverOpened={proBuilderEverOpened} layerRef={layerRef} />;
}

/**
 * Watches the content card's width (the layer's parent, which it covers) and the person's
 * "windows side by side" setting, and keeps the model's capacity in step.
 */
function useCapacityFromWidth(layerRef: { current: HTMLElement | null }): void {
  const preferred = useReadingPrefsStore((s) => s.prefs.sideBySide);
  useEffect(() => {
    const card = layerRef.current?.parentElement;
    if (!card) return;
    const update = () => {
      const store = useWindowStore.getState();
      store.setCapacity(capacityForWidth(card.clientWidth, preferred, capacityOf(store)));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(card);
    return () => observer.disconnect();
  }, [layerRef, preferred]);
}

/** The layer's markup over a given window list (the connector above reads the stores). */
export function WindowLayerView({
  windows,
  proBuilderEverOpened,
  layerRef,
}: {
  windows: readonly WindowEntry[];
  proBuilderEverOpened: boolean;
  layerRef?: { current: HTMLDivElement | null };
}) {
  const visibleCount = windows.filter((w) => w.state === 'visible').length;

  const placed = windows.filter((w) => w.state !== 'pending');
  const proBuilderListed = placed.some((w) => w.id === PRO_BUILDER_WINDOW_ID);
  const slots = [
    ...placed.map((w, i) => ({ id: w.id, visible: w.state === 'visible', column: i })),
    ...(proBuilderEverOpened && !proBuilderListed ? [{ id: PRO_BUILDER_WINDOW_ID, visible: false, column: placed.length }] : []),
  ];
  // DOM order stays the order slots first appeared; the COLUMN order follows
  // the model through CSS `order`. Re-sorting the keyed slots would move their
  // DOM nodes, and moving a node re-loads anything embedded in it (an iframe
  // terminal, say) — a swap of columns must never do that.
  const firstSeen = useRef<string[]>([]);
  for (const { id } of slots) if (!firstSeen.current.includes(id)) firstSeen.current.push(id);
  firstSeen.current = firstSeen.current.filter((id) => slots.some((s) => s.id === id));
  const domOrdered = [...slots].sort((a, b) => firstSeen.current.indexOf(a.id) - firstSeen.current.indexOf(b.id));

  return (
    <div
      ref={layerRef}
      data-testid="window-layer"
      data-visible-count={visibleCount}
      className={cn(
        // Every window covers the page, Settings included (eddy, 2026-10-26:
        // "settings should not push the inbox — it is a window like every chat").
        'absolute inset-0 z-40 gap-[var(--frame-gap)] bg-card p-[var(--frame-gap)]',
        visibleCount === 0 ? 'hidden' : 'flex',
      )}
    >
      {domOrdered.map(({ id, visible, column }) => (
        <WindowSlot key={id} id={id} visible={visible} column={column} />
      ))}
    </div>
  );
}

/**
 * A minimized window is `display: none`, and the browser drops the scroll
 * offset of everything inside it. Each scroller's position is therefore
 * remembered while the window is shown (a capture-phase scroll listener, since
 * scroll does not bubble) and put back when it is revealed again: a scroller
 * that was at the very end is sent to the real end (`bottomScrollTop`), any
 * other keeps its offset. Nothing is written while the window is hidden.
 */
function useRestoreScrollOnReveal(slotRef: { current: HTMLElement | null }, visible: boolean): void {
  const positions = useRef(new Map<Element, { top: number; atBottom: boolean }>());
  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const onScroll = (e: Event) => {
      const el = e.target;
      if (!(el instanceof HTMLElement) || el.clientHeight === 0) return; // hidden: not the reader
      positions.current.set(el, { top: el.scrollTop, atBottom: el.scrollHeight - el.scrollTop - el.clientHeight <= 2 });
    };
    slot.addEventListener('scroll', onScroll, { capture: true });
    return () => slot.removeEventListener('scroll', onScroll, { capture: true });
  }, [slotRef]);
  const wasVisible = useRef(visible);
  useLayoutEffect(() => {
    if (visible && !wasVisible.current) {
      const apply = () => {
        for (const [el, pos] of positions.current) {
          if (!el.isConnected) {
            positions.current.delete(el);
            continue;
          }
          (el as HTMLElement).scrollTop = pos.atBottom ? bottomScrollTop(el) : pos.top;
        }
      };
      apply();
      // Content settles a frame later (fonts, images); land on the floor again.
      const raf = requestAnimationFrame(apply);
      wasVisible.current = visible;
      return () => cancelAnimationFrame(raf);
    }
    wasVisible.current = visible;
  }, [visible]);
}

function WindowSlot({ id, visible, column }: { id: string; visible: boolean; column: number }) {
  const setSlot = useWindowSlots((s) => s.setSlot);
  const slotRef = useRef<HTMLDivElement | null>(null);
  useRestoreScrollOnReveal(slotRef, visible);
  // A stable ref callback: an inline one re-fires null/element on every render.
  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      slotRef.current = el;
      setSlot(id, el);
    },
    [id, setSlot],
  );
  // Selectors return a boolean / strings only — never a derived array (see
  // window-selectors.test.ts).
  const choosing = useWindowStore((s) => needsChoice(s));
  const pendingTitle = useWindowStore((s) => (s.pendingId ? findWindow(s, s.pendingId)?.title : undefined));
  const title = useWindowStore((s) => findWindow(s, id)?.title);
  return (
    <div
      ref={ref}
      data-testid="window-slot"
      data-window-id={id}
      // Hover makes a column the active one (Escape closes it) — eddy, 2026-10-26.
      onPointerEnter={() => useWindowStore.getState().focus(id)}
      onPointerDownCapture={() => useWindowStore.getState().focus(id)}
      onFocusCapture={() => useWindowStore.getState().focus(id)}
      data-column={column}
      style={{ order: column }}
      className={cn('relative min-w-0 flex-1', !visible && 'hidden')}
    >
      {choosing && visible && (
        <ReplaceTarget id={id} title={title} pendingTitle={pendingTitle} />
      )}
    </div>
  );
}

/**
 * eddy, 2026-10-26: "to select a side to replace, let me click the column".
 * While a third window waits, each visible column is covered by one big
 * target — click the column and it is minimized, the new window takes its
 * place. Escape or the hint's Cancel (window-chooser.tsx) backs out.
 */
export function ReplaceTarget({ id, title, pendingTitle }: { id: string; title?: string; pendingTitle?: string }) {
  return (
    <button
      type="button"
      data-testid="window-replace-target"
      data-window-id={id}
      aria-label={`Replace ${title || 'this window'}${pendingTitle ? ` with ${pendingTitle}` : ''}`}
      onClick={() => useWindowStore.getState().chooseReplace(id)}
      className="group absolute inset-0 z-30 flex items-center justify-center rounded-[var(--frame-radius)] border-2 border-dashed border-primary/40 bg-background/70 backdrop-blur-[2px] transition-colors hover:border-primary hover:bg-primary/10 focus-visible:border-primary focus-visible:outline-none"
    >
      {/* Icon only, no text in the column (eddy, 2026-10-26); the button's
          name says what it does for a screen reader. */}
      <Swap
        size={64}
        weight="light"
        aria-hidden="true"
        className="text-primary/60 transition-colors group-hover:text-primary group-focus-visible:text-primary"
      />
    </button>
  );
}
